import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Contact.css';

function Contact() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return value.trim().length < 2 ? 'First name must be at least 2 characters' : '';
      case 'lastName':
        return value.trim().length < 2 ? 'Last name must be at least 2 characters' : '';
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address using a proper domain (@gmail.com, etc.)' : '';
      case 'subject':
        return value.trim().length < 5 ? 'Subject must be at least 5 characters' : '';
      case 'message':
        return value.trim().length < 10 ? 'Message must be at least 10 characters' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });
    
    // Validate field on blur
    const error = validateField(name, value);
    if (error) {
      setErrors({
        ...errors,
        [name]: error
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Using Formspree for contact form handling
      const response = await fetch('https://formspree.io/f/xblypgrq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: ''
        });
        setErrors({}); // Clear any existing errors
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <header className="header">
        <div className={`container header-container ${mobileMenuOpen ? 'menu-open' : ''}`}>
          <div className="logo">
            <Link to="/">
              <img src={require('./assets/images/fixed-logo-1024.png')} alt="Yuppy NYC dog app logo" className="logo-img" />
              yuppy
            </Link>
          </div>
          <nav className={`navigation`}>
            <Link to="/" className="nav-link">HOME</Link>
            <span className="nav-separator">/</span>
            <Link to="/app" className="nav-link">DOWNLOAD YUPPY</Link>
          </nav>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}
        <nav className={`mobile-navigation ${mobileMenuOpen ? 'open' : ''}`}>
          <button 
            className="mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
          <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Home</Link>
          <Link to="/app" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Download Yuppy</Link>
        </nav>
      </header>

      <main className="contact-main">
        <div className="container">
          <div className="contact-content">
            <h1>Contact</h1>
            <p className="contact-intro">
              If you have any questions, comments, or need assistance, feel free to reach out below!
            </p>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-heading">Name</h3>
                  <span className="section-required">(required)</span>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {touched.firstName && errors.firstName && <span className="error-message-field">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {touched.lastName && errors.lastName && <span className="error-message-field">{errors.lastName}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-heading">Email Address</h3>
                  <span className="section-required">(required)</span>
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.email ? 'error' : ''}
                  />
                  {touched.email && errors.email && <span className="error-message-field">{errors.email}</span>}
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-heading">Subject</h3>
                  <span className="section-required">(required)</span>
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.subject ? 'error' : ''}
                  />
                  {touched.subject && errors.subject && <span className="error-message-field">{errors.subject}</span>}
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-heading">Message</h3>
                  <span className="section-required">(required)</span>
                </div>
                <div className="form-group">
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.message ? 'error' : ''}
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  {touched.message && errors.message && <span className="error-message-field">{errors.message}</span>}
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'SUBMIT'}
              </button>

              {submitStatus === 'success' && (
                <div className="success-screen">
                  <div className="success-content">
                    <div className="success-icon">✓</div>
                    <h2>Message Sent Successfully!</h2>
                    <p>Thank you for reaching out. We'll get back to you soon.</p>
                    <button 
                      className="new-message-button"
                      onClick={() => {
                        setSubmitStatus('');
                        setFormData({
                          firstName: '',
                          lastName: '',
                          email: '',
                          subject: '',
                          message: ''
                        });
                        setErrors({});
                        setTouched({});
                      }}
                    >
                      Send Another Message
                    </button>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="error-message">
                  Sorry, there was an error sending your message. Please try again or email us directly at yuppy@yuppy.nyc
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Contact;
